import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { adUnitAPI, inventoryAPI, API_BASE_URL } from "../services/api";
import AccountSelector from "../components/AccountSelector";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { getApiErrorMessage } from "../utils/apiError";
import {
  doesInventoryMatchSearch,
  filterCampaignEntriesForInventorySearch,
} from "../utils/inventorySearch";
import { sortSelectedFirst } from "../utils/listOrdering";
import { isAdUnitRunning } from "../utils/deliveryEligibility";
import { buildCmsScriptTag } from "../utils/cmsScript";
import "../styles/Inventory.css";

const isAdUnitLinkedToChannel = (adUnit, channelId) => {
  const targetId = String(channelId || "");
  if (!targetId) return false;

  const inventoryIds = Array.isArray(adUnit?.inventories)
    ? adUnit.inventories.map((entry) => String(entry?._id || entry))
    : [];
  const primaryInventoryId = adUnit?.inventory
    ? String(adUnit.inventory?._id || adUnit.inventory)
    : null;

  return inventoryIds.includes(targetId) || primaryInventoryId === targetId;
};

function Inventory({ searchQuery = "" }) {
  const { currentAccount, user, accounts } = useAuth();
  const [inventories, setInventories] = useState([]);
  const [adUnits, setAdUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { notifyError: setError, notifySuccess: setSuccessMessage } = useToast();
  const confirmAction = useConfirm();
  const [runningAdsOnly, setRunningAdsOnly] = useState(false);
  const [sortMode, setSortMode] = useState("name");
  const [isCmsSetupExpanded, setIsCmsSetupExpanded] = useState(false);
  const [isCreateCardExpanded, setIsCreateCardExpanded] = useState(false);
  const [isAdChannelsExpanded, setIsAdChannelsExpanded] = useState(true);
  const [selectedInventoryFilterIds, setSelectedInventoryFilterIds] = useState(
    [],
  );
  const [isInventoryFilterOpen, setIsInventoryFilterOpen] = useState(false);
  const [expandedSnippets, setExpandedSnippets] = useState({});
  const [expandedInventoryIds, setExpandedInventoryIds] = useState(
    () => new Set(),
  );
  const [inventorySummaryViewById, setInventorySummaryViewById] = useState({});
  const [form, setForm] = useState({
    name: "",
    key: "",
    description: "",
    adUnitIds: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [editingInventory, setEditingInventory] = useState(null);
  const [editBaseline, setEditBaseline] = useState({ adUnitIds: [] });
  const [editForm, setEditForm] = useState({
    name: "",
    key: "",
    description: "",
    isActive: true,
    adUnitIds: [],
  });
  const cmsScriptTag = buildCmsScriptTag(
    API_BASE_URL,
    window.location.origin,
    process.env.REACT_APP_DELIVERY_URL,
  );
  const editSessionRef = useRef(0);
  const inventoryFilterRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const loadAbortControllerRef = useRef(null);
  const loadedAccountRef = useRef(null);

  const isRunningAdUnit = useCallback((adUnit) => isAdUnitRunning(adUnit), []);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    loadAbortControllerRef.current?.abort();
    const controller = new AbortController();
    loadAbortControllerRef.current = controller;
    const accountId = currentAccount?.id || null;

    if (!accountId) {
      setInventories([]);
      setAdUnits([]);
      setLoading(false);
      setRefreshing(false);
      loadedAccountRef.current = null;
      return;
    }

    const isInitialLoad = loadedAccountRef.current !== accountId;
    if (isInitialLoad && !silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const [inventoryResponse, adUnitResponse] = await Promise.all([
        inventoryAPI.getAll({ runningAdsOnly, includeStats: true }, { signal: controller.signal }),
        adUnitAPI.getAll({ view: "summary" }, { signal: controller.signal }),
      ]);

      if (loadRequestIdRef.current !== requestId) return;

      const inventoryList = inventoryResponse.data || [];
      const adUnitList = adUnitResponse.data || [];
      const uniqueInventories = Array.from(
        new Map(inventoryList.map((item) => [item._id, item])).values(),
      );
      const uniqueAdUnits = Array.from(
        new Map(adUnitList.map((item) => [item._id, item])).values(),
      );
      setInventories(uniqueInventories);
      setAdUnits(uniqueAdUnits);
      loadedAccountRef.current = accountId;
      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      if (loadRequestIdRef.current !== requestId) return;
      if (err?.code === "ERR_CANCELED") return;
      setError("Failed to load ad channel data");
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentAccount?.id, runningAdsOnly, setError]);

  useEffect(() => () => loadAbortControllerRef.current?.abort(), []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setInventories([]);
    setAdUnits([]);
    setSelectedInventoryFilterIds([]);
    setIsInventoryFilterOpen(false);
    setExpandedSnippets({});
    setExpandedInventoryIds(new Set());
    setInventorySummaryViewById({});
    editSessionRef.current += 1;
    setEditingId(null);
    setEditingInventory(null);
    setPendingAction(null);
    setEditForm({
      name: "",
      key: "",
      description: "",
      isActive: true,
      adUnitIds: [],
    });
    setForm({
      name: "",
      key: "",
      description: "",
      adUnitIds: [],
    });
    setIsCreateCardExpanded(false);
  }, [currentAccount?.id, setError]);

  useEffect(() => {
    if (!isInventoryFilterOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (inventoryFilterRef.current?.contains(event.target)) return;
      setIsInventoryFilterOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isInventoryFilterOpen]);

  const inventoryDetailsById = useMemo(() => {
    const detailsMap = new Map();

    inventories.forEach((inventory) => {
      const linkedAdUnits = adUnits.filter((adUnit) =>
        isAdUnitLinkedToChannel(adUnit, inventory._id),
      );
      const runningAdUnits = inventory.isActive ? linkedAdUnits.filter(isRunningAdUnit) : [];

      const buildCampaignEntries = (sourceAdUnits) => {
        const campaignMap = new Map();
        sourceAdUnits.forEach((adUnit) => {
          const campaignId = String(
            adUnit?.campaign?._id ||
              adUnit?.campaign ||
              `campaign-unavailable-${adUnit._id}`,
          );
          const campaignName = adUnit?.campaign?.name || "Campaign unavailable";
          const campaignStatus = adUnit?.campaign?.status || "unavailable";
          if (!campaignMap.has(campaignId)) {
            campaignMap.set(campaignId, {
              campaignId,
              campaignName,
              campaignStatus,
              adUnits: [],
            });
          }
          campaignMap.get(campaignId).adUnits.push(adUnit);
        });
        return [...campaignMap.values()].sort((a, b) =>
          String(a.campaignName || "").localeCompare(
            String(b.campaignName || ""),
            undefined,
            { sensitivity: "base" },
          ),
        );
      };

      const linkedCampaigns = buildCampaignEntries(linkedAdUnits);
      const runningCampaigns = buildCampaignEntries(runningAdUnits);
      const linkedImpressions = linkedAdUnits.reduce(
        (sum, adUnit) => sum + Number(adUnit?.impressions || 0),
        0,
      );
      const linkedClicks = linkedAdUnits.reduce(
        (sum, adUnit) => sum + Number(adUnit?.clicks || 0),
        0,
      );
      const linkedCtr =
        linkedImpressions > 0 ? (linkedClicks / linkedImpressions) * 100 : 0;
      const runningImpressions = runningAdUnits.reduce(
        (sum, adUnit) => sum + Number(adUnit?.impressions || 0),
        0,
      );
      const runningClicks = runningAdUnits.reduce(
        (sum, adUnit) => sum + Number(adUnit?.clicks || 0),
        0,
      );
      const runningCtr =
        runningImpressions > 0 ? (runningClicks / runningImpressions) * 100 : 0;

      detailsMap.set(String(inventory._id), {
        linkedCount: linkedAdUnits.length,
        runningCount: runningAdUnits.length,
        linkedMetrics: {
          impressions: linkedImpressions,
          clicks: linkedClicks,
          ctr: linkedCtr,
        },
        runningMetrics: {
          impressions: runningImpressions,
          clicks: runningClicks,
          ctr: runningCtr,
        },
        linkedCampaigns,
        runningCampaigns,
      });
    });

    return detailsMap;
  }, [inventories, adUnits, isRunningAdUnit]);

  const sortedAdUnits = useMemo(
    () =>
      [...adUnits].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
          sensitivity: "base",
        }),
      ),
    [adUnits],
  );

  const inventoryFilterOptions = useMemo(
    () =>
      [...inventories]
        .map((inventory) => ({
          id: String(inventory?._id || ""),
          name: String(inventory?.name || "Untitled Ad Channel"),
        }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        ),
    [inventories],
  );

  useEffect(() => {
    const optionIds = new Set(inventoryFilterOptions.map((entry) => entry.id));
    setSelectedInventoryFilterIds((prev) => {
      const next = prev.filter((id) => optionIds.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [inventoryFilterOptions]);

  const selectedInventoryFilterSet = useMemo(
    () => new Set(selectedInventoryFilterIds.map((id) => String(id))),
    [selectedInventoryFilterIds],
  );

  const hasInventorySelectionFilter = selectedInventoryFilterIds.length > 0;
  const orderedInventoryFilterOptions = useMemo(
    () =>
      sortSelectedFirst(
        inventoryFilterOptions,
        (option) => selectedInventoryFilterSet.has(option.id),
      ),
    [inventoryFilterOptions, selectedInventoryFilterSet],
  );

  const sortedInventories = useMemo(() => {
    const normalizedSearch = String(searchQuery || "")
      .trim()
      .toLowerCase();
    const hasSelectionFilter = selectedInventoryFilterIds.length > 0;
    const rows = [...inventories];
    // Keep the active editor mounted even when a successful save removes the
    // channel from the running-only result or from the current title filter.
    if (editingInventory && !rows.some((row) => row._id === editingId)) {
      rows.push(editingInventory);
    }
    return rows
      .map((inventory, index) => ({ inventory, index }))
      .filter(({ inventory }) => {
        if (inventory._id === editingId) return true;
        if (!normalizedSearch) return true;
        const details = inventoryDetailsById.get(String(inventory?._id));
        if (!details) return false;
        const summaryView =
          inventorySummaryViewById[inventory?._id] || "linked";
        const campaignEntries =
          summaryView === "running"
            ? details.runningCampaigns
            : details.linkedCampaigns;

        return doesInventoryMatchSearch(
          inventory,
          campaignEntries,
          normalizedSearch,
        );
      })
      .filter(({ inventory }) => {
        if (inventory._id === editingId) return true;
        if (!hasSelectionFilter) return true;
        const inventoryId = String(inventory?._id || "");
        return selectedInventoryFilterSet.has(inventoryId);
      })
      .sort((a, b) => {
        if (sortMode === "mostActiveAdUnits") {
          const aDetails = inventoryDetailsById.get(String(a.inventory?._id));
          const bDetails = inventoryDetailsById.get(String(b.inventory?._id));
          const runningDelta =
            Number(bDetails?.runningCount || 0) -
            Number(aDetails?.runningCount || 0);
          if (runningDelta !== 0) return runningDelta;
        }
        const nameA = (a.inventory?.name || "").toLowerCase();
        const nameB = (b.inventory?.name || "").toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return a.index - b.index;
      })
      .map(({ inventory }) => inventory);
  }, [
    inventories,
    editingId,
    editingInventory,
    searchQuery,
    sortMode,
    selectedInventoryFilterIds,
    selectedInventoryFilterSet,
    inventoryDetailsById,
    inventorySummaryViewById,
  ]);

  const selectedInventoryFilterSummary = useMemo(() => {
    const selectedOptions = inventoryFilterOptions.filter((entry) =>
      selectedInventoryFilterSet.has(entry.id),
    );
    if (
      selectedOptions.length === 0 ||
      selectedOptions.length === inventoryFilterOptions.length
    ) {
      return "All Ad Channels";
    }
    if (selectedOptions.length === 1) {
      return selectedOptions[0].name;
    }
    if (selectedOptions.length === 2) {
      return `${selectedOptions[0].name}, ${selectedOptions[1].name}`;
    }
    return `${selectedOptions[0].name} +${selectedOptions.length - 1}`;
  }, [inventoryFilterOptions, selectedInventoryFilterSet]);

  const showCopiedFeedback = () => {
    setSuccessMessage("Copied to the clipboard");
  };

  const toggleSnippetExpanded = (inventoryId) => {
    setExpandedSnippets((prev) => ({
      ...prev,
      [inventoryId]: !prev[inventoryId],
    }));
  };

  const toggleInventoryExpanded = (inventoryId) => {
    const normalizedId = String(inventoryId || "");
    setExpandedInventoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(normalizedId)) {
        next.delete(normalizedId);
      } else {
        next.add(normalizedId);
      }
      return next;
    });
  };

  const getSummaryView = (inventoryId) =>
    inventorySummaryViewById[inventoryId] || "linked";

  const setSummaryView = (inventoryId, view) => {
    setInventorySummaryViewById((prev) => ({
      ...prev,
      [inventoryId]: view,
    }));
  };

  const toggleInventoryFilterSelection = (inventoryId) => {
    const normalizedId = String(inventoryId || "");
    setSelectedInventoryFilterIds((prev) => {
      const next = new Set(prev.map((id) => String(id)));
      if (next.has(normalizedId)) {
        next.delete(normalizedId);
      } else {
        next.add(normalizedId);
      }
      return [...next];
    });
  };

  const selectAllInventoryFilters = () => {
    setSelectedInventoryFilterIds(
      inventoryFilterOptions.map((option) => option.id),
    );
  };

  const clearInventoryFilters = () => {
    setSelectedInventoryFilterIds([]);
  };

  const copyToClipboard = async (text) => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(text);
      showCopiedFeedback();
      return true;
    } catch (copyError) {
      setError("Failed to copy");
      return false;
    }
  };

  const slugifyKey = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const generateCopyName = (baseName, existingNames) => {
    const clean = baseName.trim();
    const match = clean.match(/^(.*?)(?:\s+(\d+))$/);
    let base = clean;
    let num = null;

    if (match) {
      base = (match[1] || clean).trim();
      num = parseInt(match[2], 10);
    }

    if (num !== null) {
      const candidate = `${base} ${num + 1}`;
      if (!existingNames.has(candidate)) return candidate;
    }

    let i = 2;
    let candidate = `${clean} ${i}`;
    while (existingNames.has(candidate)) {
      i += 1;
      candidate = `${clean} ${i}`;
    }

    return candidate;
  };

  const generateCopyKey = (baseKey, existingKeys) => {
    const clean = slugifyKey(baseKey);
    const match = clean.match(/^(.*?)-(\d+)$/);
    let base = clean;
    let num = null;

    if (match) {
      base = match[1];
      num = parseInt(match[2], 10);
    }

    if (num !== null) {
      const candidate = `${base}-${num + 1}`;
      if (!existingKeys.has(candidate)) return candidate;
    }

    let i = 2;
    let candidate = `${clean}-${i}`;
    while (existingKeys.has(candidate)) {
      i += 1;
      candidate = `${clean}-${i}`;
    }

    return candidate;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (pendingAction) return;
    setError(null);
    setPendingAction({ type: "create", id: "new" });

    try {
      await inventoryAPI.create({
        name: form.name,
        key: form.key,
        description: form.description,
        adUnitIds: form.adUnitIds,
      });

      setForm({
        name: "",
        key: "",
        description: "",
        adUnitIds: [],
      });
      setSuccessMessage("Ad Channel created");
      await loadData({ silent: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to create ad channel"));
    } finally {
      setPendingAction(null);
    }
  };

  const startEdit = (inventory) => {
    if (pendingAction) return;
    editSessionRef.current += 1;
    const linkedIds = adUnits.filter((unit) => isAdUnitLinkedToChannel(unit, inventory._id)).map((unit) => String(unit._id));
    setEditBaseline({ updatedAt: inventory.updatedAt, adUnitIds: linkedIds });
    setEditingId(inventory._id);
    setEditingInventory(inventory);
    setEditForm({
      name: inventory.name || "",
      key: inventory.key || "",
      description: inventory.description || "",
      isActive: inventory.isActive !== false,
      adUnitIds: adUnits
        .filter((adUnit) => isAdUnitLinkedToChannel(adUnit, inventory._id))
        .map((adUnit) => String(adUnit._id)),
    });
  };

  const cancelEdit = () => {
    editSessionRef.current += 1;
    setEditingId(null);
    setEditingInventory(null);
    setEditForm({
      name: "",
      key: "",
      description: "",
      isActive: true,
      adUnitIds: [],
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (pendingAction) return;
    setError(null);
    const targetId = editingId;
    const editSession = editSessionRef.current;
    setPendingAction({ type: "update", id: targetId });

    try {
      const changed = JSON.stringify([...editForm.adUnitIds].sort()) !== JSON.stringify([...editBaseline.adUnitIds].sort());
      const { adUnitIds, ...metadata } = editForm;
      const response = await inventoryAPI.update(targetId, {
        ...metadata, _expectedUpdatedAt: editBaseline.updatedAt,
        ...(changed ? { adUnitIds, _expectedAdUnitIds: editBaseline.adUnitIds } : {}),
      });
      if (editSession !== editSessionRef.current) return;
      setEditingInventory((previous) => ({ ...previous, ...response.data }));
      // Rebase on the successful write so a second save does not send a stale
      // revision or replay old assignments. Only Cancel exits edit mode.
      setEditBaseline({ updatedAt: response.data.updatedAt, adUnitIds: [...adUnitIds] });
      setEditForm({
        name: response.data.name || "",
        key: response.data.key || "",
        description: response.data.description || "",
        isActive: response.data.isActive !== false,
        adUnitIds: [...adUnitIds],
      });
      setSuccessMessage("Ad Channel updated");
      await loadData({ silent: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to update ad channel"));
    } finally {
      setPendingAction((pending) => pending?.type === "update" && pending?.id === targetId ? null : pending);
    }
  };

  const handleDelete = async (inventory) => {
    if (pendingAction) return;
    const linkedAdUnits = adUnits.filter((adUnit) =>
      isAdUnitLinkedToChannel(adUnit, inventory._id),
    );
    if (linkedAdUnits.length > 0) {
      const activeAdUnitCount = linkedAdUnits.filter(
        (adUnit) => String(adUnit?.status || "").toLowerCase() === "active",
      ).length;
      const activeDetail = activeAdUnitCount > 0
        ? `${activeAdUnitCount} active Ad Unit${activeAdUnitCount === 1 ? " is" : "s are"} still linked.`
        : `${linkedAdUnits.length} Ad Unit${linkedAdUnits.length === 1 ? " is" : "s are"} still linked.`;
      setError(
        `Ad Channel cannot be deleted while it has linked Ad Units. ${activeDetail} Edit the Ad Channel, uncheck all Ad Units, and save before deleting it.`,
      );
      return;
    }

    const confirmed = await confirmAction({
      title: "Delete ad channel?",
      message: `The ad channel "${inventory.name}" will be permanently deleted.`,
      confirmLabel: "Delete channel",
    });
    if (!confirmed) return;
    setError(null);

    try {
      setPendingAction({ type: "delete", id: inventory._id });
      await inventoryAPI.delete(inventory._id);
      setSuccessMessage("Ad Channel deleted");
      await loadData({ silent: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to delete ad channel"));
    } finally {
      setPendingAction(null);
    }
  };

  const handleDuplicate = async (inventory) => {
    if (pendingAction) return;
    setError(null);
    setPendingAction({ type: "duplicate", id: inventory._id });

    try {
      const existingNames = new Set(inventories.map((item) => item.name));
      const existingKeys = new Set(inventories.map((item) => item.key));
      const name = generateCopyName(inventory.name, existingNames);
      const key = generateCopyKey(inventory.key, existingKeys);

      await inventoryAPI.create({
        name,
        key,
        description: inventory.description || "",
      });

      setSuccessMessage("Ad Channel duplicated");
      await loadData({ silent: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to duplicate ad channel"));
    } finally {
      setPendingAction(null);
    }
  };

  const createAdUnitSelection = useMemo(
    () => new Set(form.adUnitIds.map((id) => String(id))),
    [form.adUnitIds],
  );
  const editAdUnitSelection = useMemo(
    () => new Set(editForm.adUnitIds.map((id) => String(id))),
    [editForm.adUnitIds],
  );
  const createAdUnitOptions = useMemo(
    () =>
      sortSelectedFirst(
        sortedAdUnits,
        (adUnit) => createAdUnitSelection.has(String(adUnit?._id || "")),
      ),
    [createAdUnitSelection, sortedAdUnits],
  );
  const editAdUnitOptions = useMemo(
    () =>
      sortSelectedFirst(
        sortedAdUnits,
        (adUnit) => editBaseline.adUnitIds.includes(String(adUnit?._id || "")),
      ),
    [editBaseline.adUnitIds, sortedAdUnits],
  );

  if (
    user?.role === "editor" &&
    (!Array.isArray(accounts) || accounts.length === 0 || !currentAccount?.id)
  ) {
    return (
      <div className="inventory-page">
        <div className="no-data">
          No account has been shared with you yet. Please contact the account
          owner.
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading ad channels...</div>;

  return (
    <div className="inventory-page">
      <header className="inventory-header">
        <div className="inventory-header-copy">
          <h1>Ad Channels</h1>
          <p>Create and manage ad placement channels.</p>
        </div>
        <div className="inventory-header-actions">
          <span
            className={`background-refresh-status${refreshing ? " is-visible" : ""}`}
            role="status"
            aria-live="polite"
          >
            Refreshing…
          </span>
          <AccountSelector />
        </div>
      </header>

      <div className={`ad-unit-cms ${isCmsSetupExpanded ? "is-expanded" : ""}`}>
        <button
          type="button"
          className="ad-unit-cms-toggle"
          aria-expanded={isCmsSetupExpanded}
          onClick={() => setIsCmsSetupExpanded((prev) => !prev)}
        >
          <span className="ad-unit-cms-label">CMS Setup</span>
          <span className="ad-unit-cms-toggle-icon" aria-hidden="true">
            {isCmsSetupExpanded ? "▾" : "▸"}
          </span>
        </button>
        {isCmsSetupExpanded && (
          <div className="ad-unit-cms-body">
            <div className="ad-unit-cms-description">
              Add this script inside the {`<Head>`} element of your website or
              CMS template.
            </div>
            {cmsScriptTag ? (
              <code className="ad-unit-cms-code">{cmsScriptTag}</code>
            ) : (
              <p role="alert">The public delivery URL is not configured correctly. Configure REACT_APP_API_URL or REACT_APP_DELIVERY_URL before copying the CMS tag.</p>
            )}
            <div className="ad-unit-cms-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!cmsScriptTag}
                onClick={async () => {
                  await copyToClipboard(`${cmsScriptTag}\n`);
                }}
              >
                Copy CMS Tag
              </button>
            </div>
          </div>
        )}
      </div>
      <div
        className={`inventory-card create-ad-channel-card ${isCreateCardExpanded ? "is-expanded" : "is-collapsed"}`}
      >
        <button
          type="button"
          className="ad-unit-cms-toggle create-ad-channel-toggle"
          aria-expanded={isCreateCardExpanded}
          onClick={() => setIsCreateCardExpanded((prev) => !prev)}
        >
          <h3>Create Ad Channel</h3>
          <span className="ad-unit-cms-toggle-icon" aria-hidden="true">
            {isCreateCardExpanded ? "▾" : "▸"}
          </span>
        </button>
        {isCreateCardExpanded && (
          <div className="create-ad-channel-body">
            <form onSubmit={handleCreate} className="inventory-form">
              <label className="inventory-field">
                <span>Name</span>
                <input
                  type="text"
                  placeholder="e.g., Homepage Banner"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="inventory-field">
                <span>Key <small>Optional</small></span>
                <input
                  type="text"
                  placeholder="e.g., homepage-banner"
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                />
              </label>
              <label className="inventory-field">
                <span>Description <small>Optional</small></span>
                <input
                  type="text"
                  placeholder="Describe where this channel is used"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </label>
              <div className="inventory-form-adunits">
                <div className="inventory-form-adunits-header">
                  <div className="inventory-form-adunits-label">Ad Units</div>
                  <span>{form.adUnitIds.length} selected</span>
                </div>
                {sortedAdUnits.length === 0 ? (
                  <p className="no-data">No ad units available.</p>
                ) : (
                  <div className="selectable-checkbox-list inventory-adunit-list">
                    {createAdUnitOptions.map((adUnit) => {
                      const adUnitId = String(adUnit._id);
                      const checked = createAdUnitSelection.has(adUnitId);
                      const campaignName =
                        adUnit?.campaign?.name || "Campaign unavailable";
                      return (
                        <label
                          key={adUnitId}
                          className={`selectable-checkbox-item ${checked ? "selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((prev) => {
                                const set = new Set(
                                  prev.adUnitIds.map((id) => String(id)),
                                );
                                if (set.has(adUnitId)) set.delete(adUnitId);
                                else set.add(adUnitId);
                                return { ...prev, adUnitIds: [...set] };
                              });
                            }}
                          />
                          <span className="inventory-adunit-choice-text">
                            <span className="inventory-adunit-choice-name">
                              {adUnit.name}
                            </span>
                            <span className="inventory-adunit-choice-meta">
                              Campaign: {campaignName}
                            </span>
                            <span className="inventory-adunit-choice-meta">
                              Status: {adUnit.status || "unknown"}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={Boolean(pendingAction)}
              >
                {pendingAction?.type === "create" ? "Creating…" : "Create"}
              </button>
            </form>
          </div>
        )}
      </div>

      <section
        className={`inventory-card inventory-list-card ${isAdChannelsExpanded ? "is-expanded" : "is-collapsed"}`}
      >
        <div className="inventory-list-card-header">
          <button
            type="button"
            className="ad-unit-cms-toggle inventory-list-card-toggle"
            aria-expanded={isAdChannelsExpanded}
            onClick={() => setIsAdChannelsExpanded((prev) => !prev)}
          >
            <h3>Ad Channel List</h3>
            <span className="ad-unit-cms-toggle-icon" aria-hidden="true">
              {isAdChannelsExpanded ? "▾" : "▸"}
            </span>
          </button>
          <div className="inventory-toolbar">
            <div className="inventory-toolbar-group">
              <label htmlFor="running-ads-only">Running Ads</label>
              <select
                id="running-ads-only"
                value={runningAdsOnly ? "yes" : "no"}
                onChange={(e) => setRunningAdsOnly(e.target.value === "yes")}
              >
                <option value="no">All Ad Channels</option>
                <option value="yes">Show only running ads</option>
              </select>
            </div>
            <div className="inventory-toolbar-group">
              <label htmlFor="inventory-sort-mode">Sort By</label>
              <select
                id="inventory-sort-mode"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
              >
                <option value="name">Ad Channel Name</option>
                <option value="mostActiveAdUnits">Most Active Ad Units</option>
              </select>
            </div>
            <div className="inventory-toolbar-group">
              <label htmlFor="inventory-visibility-filter">Filter Ad Channels</label>
              <div
                ref={inventoryFilterRef}
                className="inventory-filter-dropdown"
                id="inventory-visibility-filter"
              >
                <button
                  type="button"
                  className="inventory-filter-trigger"
                  aria-expanded={isInventoryFilterOpen}
                  aria-haspopup="true"
                  onClick={() => setIsInventoryFilterOpen((prev) => !prev)}
                >
                  <span>{selectedInventoryFilterSummary}</span>
                  <span aria-hidden="true">
                    {isInventoryFilterOpen ? "▾" : "▸"}
                  </span>
                </button>
                {isInventoryFilterOpen && (
                  <div className="inventory-filter-menu">
                    <div className="inventory-filter-menu-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={selectAllInventoryFilters}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={clearInventoryFilters}
                      >
                        Clear
                      </button>
                    </div>
                    {inventoryFilterOptions.length === 0 ? (
                      <p className="no-data">No Ad Channels available.</p>
                    ) : (
                      <div className="inventory-filter-options">
                        {orderedInventoryFilterOptions.map((option) => {
                          const checked = selectedInventoryFilterSet.has(
                            option.id,
                          );
                          return (
                            <label
                              key={option.id}
                              className="inventory-filter-option"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  toggleInventoryFilterSelection(option.id)
                                }
                              />
                              <span>{option.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isAdChannelsExpanded && (
          <>
            <div className="inventory-list">
              {sortedInventories.length === 0 && (
                <p className="no-data">
                  {hasInventorySelectionFilter
                    ? "No Ad Channels match the selected filters."
                    : String(searchQuery || "").trim()
                    ? "No Ad Channels found for this search."
                    : runningAdsOnly
                      ? "No Ad Channels with running ads found."
                      : "No Ad Channels found for this account."}
                </p>
              )}
              {sortedInventories.map((inventory) => (
                <div
                  key={inventory._id}
                  className={`inventory-item inventory-channel-card ${
                    expandedInventoryIds.has(String(inventory._id))
                      ? "is-expanded"
                      : "is-collapsed"
                  }`}
                >
                  {editingId === inventory._id ? (
                    <form
                      onSubmit={handleUpdate}
                      className="inventory-edit-form"
                    >
                      <fieldset disabled={Boolean(pendingAction)} style={{ display: "contents" }}>
                      <label className="inventory-field">
                        <span>Name</span>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm({ ...editForm, name: e.target.value })
                          }
                          required
                        />
                      </label>
                      <label className="inventory-field">
                        <span>Key</span>
                        <input
                          type="text"
                          value={editForm.key}
                          onChange={(e) =>
                            setEditForm({ ...editForm, key: e.target.value })
                          }
                        />
                      </label>
                      <label className="inventory-field">
                        <span>Description</span>
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              description: e.target.value,
                            })
                          }
                        />
                      </label>
                      <label className="inventory-toggle">
                        <input
                          type="checkbox"
                          checked={editForm.isActive}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              isActive: e.target.checked,
                            })
                          }
                        />
                        Active
                      </label>
                      <div className="inventory-form-adunits inventory-form-adunits-inline">
                        <div className="inventory-form-adunits-header">
                          <div className="inventory-form-adunits-label">
                            Ad Units
                          </div>
                          <span>{editForm.adUnitIds.length} selected</span>
                        </div>
                        {sortedAdUnits.length === 0 ? (
                          <p className="no-data">No ad units available.</p>
                        ) : (
                          <div className="selectable-checkbox-list inventory-adunit-list">
                            {editAdUnitOptions.map((adUnit) => {
                              const adUnitId = String(adUnit._id);
                              const checked = editAdUnitSelection.has(adUnitId);
                              const campaignName =
                                adUnit?.campaign?.name ||
                                "Campaign unavailable";
                              return (
                                <label
                                  key={adUnitId}
                                  className={`selectable-checkbox-item ${checked ? "selected" : ""}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      setEditForm((prev) => {
                                        const set = new Set(
                                          prev.adUnitIds.map((id) =>
                                            String(id),
                                          ),
                                        );
                                        if (set.has(adUnitId))
                                          set.delete(adUnitId);
                                        else set.add(adUnitId);
                                        return { ...prev, adUnitIds: [...set] };
                                      });
                                    }}
                                  />
                                  <span className="inventory-adunit-choice-text">
                                    <span className="inventory-adunit-choice-name">
                                      {adUnit.name}
                                    </span>
                                    <span className="inventory-adunit-choice-meta">
                                      Campaign: {campaignName}
                                    </span>
                                    <span className="inventory-adunit-choice-meta">
                                      Status: {adUnit.status || "unknown"}
                                    </span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="inventory-edit-actions">
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                          disabled={Boolean(pendingAction)}
                        >
                          {pendingAction?.type === "update" &&
                          pendingAction?.id === inventory._id
                            ? "Saving…"
                            : "Save Changes"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={cancelEdit}
                          disabled={Boolean(pendingAction)}
                        >
                          Cancel
                        </button>
                      </div>
                      </fieldset>
                    </form>
                  ) : (
                    <>
                      {(() => {
                        const details = inventoryDetailsById.get(
                          String(inventory._id),
                        ) || {
                          linkedCount: 0,
                          runningCount: 0,
                          linkedMetrics: { impressions: 0, clicks: 0, ctr: 0 },
                          runningMetrics: { impressions: 0, clicks: 0, ctr: 0 },
                          linkedCampaigns: [],
                          runningCampaigns: [],
                        };
                        const summaryView = getSummaryView(inventory._id);
                        const unfilteredCampaigns =
                          summaryView === "running"
                            ? details.runningCampaigns
                            : details.linkedCampaigns;
                        const currentCampaigns =
                          filterCampaignEntriesForInventorySearch(
                            unfilteredCampaigns,
                            searchQuery,
                            inventory.name,
                          );
                        const isSnippetExpanded = Boolean(
                          expandedSnippets[inventory._id],
                        );
                        const isExpanded = expandedInventoryIds.has(
                          String(inventory._id),
                        );

                        return (
                          <>
                            <button
                              type="button"
                              className="ad-unit-cms-toggle inventory-channel-toggle"
                              aria-expanded={isExpanded}
                              onClick={() =>
                                toggleInventoryExpanded(inventory._id)
                              }
                            >
                              <span className="ad-unit-cms-label inventory-channel-toggle-label">
                                <span className="inventory-name">
                                  {inventory.name}
                                </span>
                                <span className="inventory-inline-metrics">
                                  <span title="Lifetime impressions delivered on this Ad Channel">
                                    Channel Impressions:{" "}
                                    {new Intl.NumberFormat("en-US").format(
                                      inventory.deliveryStats?.impressions || 0,
                                    )}
                                  </span>
                                  <span>
                                    Channel Clicks:{" "}
                                    {new Intl.NumberFormat("en-US").format(
                                      inventory.deliveryStats?.clicks || 0,
                                    )}
                                  </span>
                                  <span>
                                    CTR:{" "}
                                    {Number(inventory.deliveryStats?.ctr || 0).toFixed(2)}
                                    %
                                  </span>
                                </span>
                              </span>
                              <span
                                className="ad-unit-cms-toggle-icon"
                                aria-hidden="true"
                              >
                                {isExpanded ? "▾" : "▸"}
                              </span>
                            </button>
                            {!isExpanded && (
                              <div className="inventory-collapsed-summary">
                                <span>
                                  Linked Ad Units: {details.linkedCount}
                                </span>
                                <span>
                                  Running Ad Units: {details.runningCount}
                                </span>
                              </div>
                            )}
                            {isExpanded && (
                              <div className="inventory-channel-body">
                                <div className="inventory-actions">
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    disabled={Boolean(pendingAction)}
                                    onClick={() => handleDuplicate(inventory)}
                                  >
                                    Duplicate
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    disabled={Boolean(pendingAction)}
                                    onClick={() => startEdit(inventory)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    disabled={Boolean(pendingAction)}
                                    onClick={() => handleDelete(inventory)}
                                  >
                                    Delete
                                  </button>
                                </div>
                                <div className="inventory-main">
                                  <div className="inventory-info">
                                    <div className="inventory-key">
                                      Key: {inventory.key}
                                    </div>
                                    {inventory.description && (
                                      <div className="inventory-desc">
                                        {inventory.description}
                                      </div>
                                    )}
                                    <div
                                      className={`inventory-status ${inventory.isActive ? "active" : "inactive"}`}
                                    >
                                      {inventory.isActive
                                        ? "Active"
                                        : "Inactive"}
                                    </div>
                                    <div className="inventory-performance">
                                      <div
                                        className="inventory-performance-summary"
                                        role="tablist"
                                        aria-label="Ad unit scope"
                                      >
                                        <button
                                          type="button"
                                          role="tab"
                                          aria-selected={
                                            summaryView === "linked"
                                          }
                                          className={`inventory-summary-toggle ${summaryView === "linked" ? "is-active" : ""}`}
                                          onClick={() =>
                                            setSummaryView(
                                              inventory._id,
                                              "linked",
                                            )
                                          }
                                        >
                                          Linked Ad Units: {details.linkedCount}
                                        </button>
                                        <button
                                          type="button"
                                          role="tab"
                                          aria-selected={
                                            summaryView === "running"
                                          }
                                          className={`inventory-summary-toggle ${summaryView === "running" ? "is-active" : ""}`}
                                          onClick={() =>
                                            setSummaryView(
                                              inventory._id,
                                              "running",
                                            )
                                          }
                                        >
                                          Running Ad Units:{" "}
                                          {details.runningCount}
                                        </button>
                                      </div>
                                      {currentCampaigns.length === 0 ? (
                                        <div className="inventory-performance-empty">
                                          {summaryView === "running"
                                            ? "No running ad units in this ad channel."
                                            : "No linked ad units in this ad channel."}
                                        </div>
                                      ) : (
                                        <div className="inventory-campaign-list">
                                          {currentCampaigns.map(
                                            (campaignEntry) => (
                                              <div
                                                key={campaignEntry.campaignId}
                                                className="inventory-campaign-item"
                                              >
                                                <div className="inventory-campaign-title">
                                                  Campaign:{" "}
                                                  {campaignEntry.campaignName}
                                                </div>
                                                <div className="inventory-campaign-status">
                                                  Status:{" "}
                                                  {campaignEntry.campaignStatus}
                                                </div>
                                                {campaignEntry.adUnits.map(
                                                  (adUnit) => (
                                                    <div
                                                      key={adUnit._id}
                                                      className="inventory-adunit-line"
                                                    >
                                                      <span>
                                                        Ad Unit: {adUnit.name}
                                                      </span>
                                                      <span>
                                                        Status: {adUnit.status}
                                                      </span>
                                                      <span>
                                                        Impressions:{" "}
                                                        {new Intl.NumberFormat(
                                                          "en-US",
                                                        ).format(
                                                          Number(
                                                            adUnit.impressions ||
                                                              0,
                                                          ),
                                                        )}
                                                      </span>
                                                      <span>
                                                        Clicks:{" "}
                                                        {new Intl.NumberFormat(
                                                          "en-US",
                                                        ).format(
                                                          Number(
                                                            adUnit.clicks || 0,
                                                          ),
                                                        )}
                                                      </span>
                                                      <span>
                                                        CTR:{" "}
                                                        {Number(
                                                          adUnit.ctr || 0,
                                                        ).toFixed(2)}
                                                        %
                                                      </span>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <div
                                      className={`inventory-snippet ${isSnippetExpanded ? "is-expanded" : ""}`}
                                    >
                                      <button
                                        type="button"
                                        className="inventory-snippet-toggle"
                                        aria-expanded={isSnippetExpanded}
                                        onClick={() =>
                                          toggleSnippetExpanded(inventory._id)
                                        }
                                      >
                                        <span className="inventory-snippet-label">
                                          CMS Tag
                                        </span>
                                        <span
                                          className="inventory-snippet-toggle-icon"
                                          aria-hidden="true"
                                        >
                                          {isSnippetExpanded ? "▾" : "▸"}
                                        </span>
                                      </button>
                                      {isSnippetExpanded && (
                                        <div className="inventory-snippet-body">
                                          <code>{`<div data-inventory="${inventory.key}" data-inventory-id="${inventory._id}" data-width="100%"></div>`}</code>
                                          <div className="inventory-snippet-actions">
                                            <button
                                              type="button"
                                              className="btn btn-secondary btn-sm"
                                              onClick={async () => {
                                                await copyToClipboard(
                                                  `<div data-inventory="${inventory.key}" data-inventory-id="${inventory._id}" data-width="100%"></div>`,
                                                );
                                              }}
                                            >
                                              Copy Tag
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default Inventory;
